import moment from 'moment';

import {
  DeltaIntervalType,
  ProceduralJurisdiction,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import type { ExtractionState } from '@/api/hooks/useProceduralStream';
import { CountingKey } from '@/new/stores/timeline-calculator-store';

/**
 * The per-phase progress detail (counts, save breakdown, completion message)
 * for an in-flight extraction. Shared by the full-page loader and the inline
 * progress banner so the two stay in lockstep. The phase *labels* live with
 * each component because they're phrased differently (sentences vs. chips).
 */
export function getExtractionDetail(state: ExtractionState): string | null {
  switch (state.phase) {
    case 'reading':
      return state.chunkCount !== null ? `${state.chunkCount} sections found` : null;
    case 'extracting':
      return state.eventCount !== null ? `${state.eventCount} event${state.eventCount === 1 ? '' : 's'} so far` : null;
    case 'merging':
      return state.decisionCount !== null
        ? `${state.decisionCount} comparison${state.decisionCount === 1 ? '' : 's'} so far`
        : null;
    case 'saving': {
      if (!state.persisted) return null;
      const parts: string[] = [];
      if (state.persisted.adds) parts.push(`${state.persisted.adds} new`);
      if (state.persisted.updates) parts.push(`${state.persisted.updates} updated`);
      if (state.persisted.skipped) parts.push(`${state.persisted.skipped} skipped`);
      return parts.join(' · ') || null;
    }
    case 'done':
      return 'Review the suggestions below.';
    case 'failed':
      return state.error;
  }
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return 'Date TBD';
  return moment(date).format('MMM DD, YYYY');
}

export function formatDateRange(start: Date | string | null, end: Date | string | null): string | null {
  if (!start) return null;
  const s = moment(start);
  const startHasTime = s.hour() !== 0 || s.minute() !== 0;
  const startStr = s.format(startHasTime ? 'MMM DD, YYYY [at] h:mm A' : 'MMM DD, YYYY');
  if (!end) return startStr;
  const e = moment(end);
  const endHasTime = e.hour() !== 0 || e.minute() !== 0;
  const sameDay = s.isSame(e, 'day');
  if (sameDay) {
    if (!startHasTime && !endHasTime) return startStr;
    return `${startStr} – ${e.format('h:mm A')}`;
  }
  const endStr = e.format(endHasTime ? 'MMM DD, YYYY [at] h:mm A' : 'MMM DD, YYYY');
  return `${startStr} – ${endStr}`;
}

/**
 * Reverse of {@link mapDeltaTypeToCounting}: resolve a calculator counting
 * selection back to the `(deltaIntervalType, jurisdiction)` pair we persist on
 * the event. Keyed by `CountingKey` so adding a new calculator option forces a
 * matching entry here at compile time.
 */
const COUNTING_TO_EVENT: Record<
  CountingKey,
  { deltaIntervalType: DeltaIntervalType; jurisdiction: ProceduralJurisdiction }
> = {
  'uk-ew-calendar': {
    deltaIntervalType: DeltaIntervalType.Calendar,
    jurisdiction: ProceduralJurisdiction.EnglandAndWales,
  },
  'uk-ew-clear-cpr': {
    deltaIntervalType: DeltaIntervalType.Clear,
    jurisdiction: ProceduralJurisdiction.EnglandAndWales,
  },
  'uk-ew-business': {
    deltaIntervalType: DeltaIntervalType.Working,
    jurisdiction: ProceduralJurisdiction.EnglandAndWales,
  },
  'us-federal-calendar': {
    deltaIntervalType: DeltaIntervalType.Calendar,
    jurisdiction: ProceduralJurisdiction.USFederal,
  },
  'us-federal-business': {
    deltaIntervalType: DeltaIntervalType.Working,
    jurisdiction: ProceduralJurisdiction.USFederal,
  },
};

export function mapCountingToEvent(key: CountingKey): {
  deltaIntervalType: DeltaIntervalType;
  jurisdiction: ProceduralJurisdiction;
} {
  return COUNTING_TO_EVENT[key];
}

/**
 * Seed the calculator's counting selector from the event's day-count type +
 * jurisdiction. Unsupported jurisdictions (Other / null) fall back to the
 * UK E&W calendar default so the calculator still opens to something usable.
 */
export function mapDeltaTypeToCounting(
  type: DeltaIntervalType,
  jurisdiction: ProceduralJurisdiction | null,
): CountingKey {
  if (jurisdiction === ProceduralJurisdiction.USFederal) {
    switch (type) {
      case DeltaIntervalType.Working:
      case DeltaIntervalType.Court:
        return 'us-federal-business';
      case DeltaIntervalType.Calendar:
      case DeltaIntervalType.Clear:
      default:
        return 'us-federal-calendar';
    }
  }
  switch (type) {
    case DeltaIntervalType.Clear:
      return 'uk-ew-clear-cpr';
    case DeltaIntervalType.Working:
    case DeltaIntervalType.Court:
      return 'uk-ew-business';
    case DeltaIntervalType.Calendar:
    default:
      return 'uk-ew-calendar';
  }
}
